import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import qs from 'query-string';

import { userSelectors } from 'reducers';
import {
    useExportSubscriptionsMutation,
    useGetRolesQuery,
    useGetShareSubcategoriesQuery,
    useGetSubscriptionsQuery,
} from 'api/main';
import {
    ORDER_BY,
    roleCategories,
    RoleNames,
    servicerRoles,
    UserEnrichments,
    sortDirection,
    queryStringifyOptions,
    listingActionButtonStyle,
    promoterRoles,
} from 'utils/constants';
import {
    useDebounce,
    useDialog,
    useLocalStorageFilters,
    useMatchAtLeastOneRole,
} from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import SubscriptionInvite from './SubscriptionInvite';
import CustomFilters from './CustomFilters';
import SubscriptionRow from './SubscriptionRow';
import CreateSubscriptionDialog from './CreateSubscriptionDialog';
import {
    ConfirmDialog,
    CustomTablePagination,
    EmptyTableBody,
    EnhancedTableHead,
    QuickSearchField,
    ActionButton,
    FiltersButton,
} from 'components';
import {
    Typography,
    TableRow,
    Table,
    TableBody,
    TableContainer,
    TableFooter,
    Box,
} from '@mui/material';

const tableHeads = [
    {
        id: ORDER_BY.alerts,
        width: 6,
        isSortable: true,
        label: texts.ALERTS.ALERTS,
        align: 'center',
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.lastNameAndCompany,
        width: 12,
        isSortable: true,
        label: texts.NOM,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.role,
        width: 9,
        isSortable: true,
        label: texts.PROFIL_INVESTISSEUR,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.subscriptionCreatedAt,
        width: 10,
        isSortable: true,
        label: texts.DATE_ENTREE,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.investmentSum,
        width: 12,
        isSortable: true,
        label: texts.MONTANT,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: 'admissionFee',
        width: 5,
        isSortable: false,
        label: 'DE',
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.fundName,
        width: 12,
        isSortable: true,
        label: texts.PRODUIT,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.shareCategory,
        width: 8,
        isSortable: true,
        label: texts.CATEGORIE_DE_PARTS,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.tunnelStep,
        width: 11,
        isSortable: false,
        label: texts.ETAPE_ACTUELLE,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.distributorCompanyName,
        width: 10,
        isSortable: true,
        label: texts.DISTRIBUTEUR,
        availableFor: ['servicer'],
    },
    {
        id: ORDER_BY.distributorFullName,
        width: 10,
        isSortable: true,
        label: texts.CONSULTANT,
        availableFor: ['distributor'],
    },
    {
        id: 'actions',
        width: 3,
        isSortable: false,
        label: '',
        availableFor: ['servicer', 'distributor'],
    },
];

const filtersKey = 'subscriptionFilters';

const defaultEnrichments = [
    UserEnrichments.ALERTS,
    UserEnrichments.USER_TUNNEL_STEPS,
    UserEnrichments.INVESTOR_PROFILE,
    UserEnrichments.INVESTMENT_FUND,
    UserEnrichments.INVESTMENT_CHARACTERISTICS,
    UserEnrichments.SHARE_SUBCATEGORY,
    UserEnrichments.SHARE_CATEGORY,
    UserEnrichments.ROLES,
    UserEnrichments.COMPANY,
    UserEnrichments.SUBSCRIPTIONS,
    UserEnrichments.SIGNATURES,
    UserEnrichments.BANKING_STATEMENT,
    UserEnrichments.DISTRIBUTOR_COMPANY,
    UserEnrichments.CREATED_BY,
];

const SubscriptionTable = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const { isLoading: shareSubcategoriesLoader } = useGetShareSubcategoriesQuery();
    const [exportSubscriptions, { isLoading: exportLoading }] = useExportSubscriptionsMutation();

    const internalUser = useSelector(userSelectors.getInternalUser);

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [newSubscriptionOpen, setNewSubscriptionOpen] = useState(false);
    const [createSubscriptionOpen, setCreateSubscriptionOpen] = useState(false);
    const {
        open: alertTunnelChangesOpen,
        onClose: handleAlertTunnelChangesClose,
        onOpen: handleAlertTunnelChangesOpen,
    } = useDialog();

    const userIsDistributor = useMatchAtLeastOneRole([
        RoleNames.ADMIN_CIF,
        RoleNames.COMMERCIALISATEUR,
        RoleNames.PSI,
    ]);
    const userIsNetworkAdmin = useMatchAtLeastOneRole([RoleNames.NETWORK_ADMIN]);

    const {
        orderBy,
        direction,
        product,
        dist,
        companyId = null,
        investorClassification,
        fundSGPId,
        pendingActivation = false,
        pendingTransfer = false,
        pendingCIFValidation = false,
        pendingServicerValidation = false,
        pendingSGPSignature = false,
        transferred = false,
        transferred03 = false,
        transferred37 = false,
        transferred730 = false,
        transferred30plus = false,
        activity_0_3 = false,
        activity_3_7 = false,
        activity_7_30 = false,
        activity_30_plus = false,
        alertTypes = [],
    } = useMemo(() => qs.parse(location.search, queryStringifyOptions), [location]);
    const activeImpersonation = useMemo(() => !!internalUser?.impersonatedByUser, [internalUser]);
    const investorRoles = useMemo(
        () => roles.filter(r => r.category?.name === roleCategories.INVESTORS),
        [roles]
    );
    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 500);

    const defaultFilters = useMemo(
        () => ({
            page: 1,
            role: [],
            orderBy: orderBy || ORDER_BY.subscriptionId,
            direction: direction || sortDirection.DESC,
            subscriptionStatus: ['in_progress'],
            alertTypes: alertTypes.length ? alertTypes : 'all',
            product,
            dist,
            investorClassification,
            fundSGPId,
            companyId,
            pendingActivation: pendingActivation ? 1 : undefined,
            pendingTransfer: pendingTransfer ? 1 : undefined,
            pendingServicerValidation: pendingServicerValidation ? 1 : undefined,
            pendingCIFValidation: pendingCIFValidation ? 1 : undefined,
            pendingSGPSignature: pendingSGPSignature ? 1 : undefined,
            transferred: transferred ? 1 : undefined,
            transferred03: transferred03 ? 1 : undefined,
            transferred37: transferred37 ? 1 : undefined,
            transferred730: transferred730 ? 1 : undefined,
            transferred30plus: transferred30plus ? 1 : undefined,
            activity_0_3: activity_0_3 ? 1 : undefined,
            activity_3_7: activity_3_7 ? 1 : undefined,
            activity_7_30: activity_7_30 ? 1 : undefined,
            activity_30_plus: activity_30_plus ? 1 : undefined,
            keyword,
            enrichWith: defaultEnrichments,
        }),
        [
            keyword,
            product,
            dist,
            investorClassification,
            fundSGPId,
            pendingActivation,
            pendingTransfer,
            pendingCIFValidation,
            pendingServicerValidation,
            pendingSGPSignature,
            transferred,
            transferred03,
            transferred37,
            transferred730,
            transferred30plus,
            alertTypes,
            direction,
            orderBy,
            activity_0_3,
            activity_3_7,
            activity_7_30,
            activity_30_plus,
            companyId,
        ]
    );
    const handleCreateSubscriptionOpen = useCallback(() => {
        handleAlertTunnelChangesClose();
        setCreateSubscriptionOpen(true);
    }, [handleAlertTunnelChangesClose]);

    const hasOneTimeFilters = useMemo(() => !!location.search, [location]);

    const [filters, setFilters] = useLocalStorageFilters(
        filtersKey,
        defaultFilters,
        activeImpersonation || hasOneTimeFilters || !!debouncedKeyword
    );

    const {
        data: { subscriptions: subscriptionList = [], lastPage } = {
            subscriptions: [],
            lastPage: 1,
        },
        isFetching: listLoader,
    } = useGetSubscriptionsQuery(
        { role: investorRoles.map(role => role.id), ...filters },
        {
            skip: !roles.length,
        }
    );

    useEffect(() => {
        setFilters(prevState => ({
            ...prevState,
            keyword: debouncedKeyword,
            orderBy:
                !!debouncedKeyword || hasOneTimeFilters
                    ? prevState.orderBy
                    : ORDER_BY.subscriptionId,
            direction:
                !!debouncedKeyword || hasOneTimeFilters ? prevState.direction : sortDirection.DESC,
            page: 1,
            role: !prevState.role.length ? investorRoles.map(role => role.id) : prevState.role,
        }));
    }, [debouncedKeyword, setFilters, hasOneTimeFilters, investorRoles]);

    useEffect(() => {
        // if all one time filters are removed from URL we should return to the previous filters(localStorage or default)
        if (!hasOneTimeFilters) {
            setFilters(prevState => ({
                ...prevState,
                alertTypes: [],
                pendingActivation: undefined,
                pendingTransfer: undefined,
                pendingServicerValidation: undefined,
                pendingCIFValidation: undefined,
                pendingSGPSignature: undefined,
                transferred: undefined,
                transferred03: undefined,
                transferred37: undefined,
                transferred730: undefined,
                transferred30plus: undefined,
                activity_0_3: undefined,
                activity_3_7: undefined,
                activity_7_30: undefined,
                activity_30_plus: undefined,
            }));
        }
    }, [setFilters, hasOneTimeFilters]);

    const userIsServicer = useMatchAtLeastOneRole(servicerRoles);
    const userIsPromoter = useMatchAtLeastOneRole(promoterRoles);
    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);

    const filteredColumns = useMemo(
        () =>
            tableHeads.filter(h =>
                h.availableFor.includes(userIsDistributor ? 'distributor' : 'servicer')
            ),
        [userIsDistributor]
    );

    const dataIsLoading = useMemo(
        () => rolesLoader || listLoader || shareSubcategoriesLoader,
        [rolesLoader, listLoader, shareSubcategoriesLoader]
    );

    useEffect(() => {
        if (filters.page > lastPage && lastPage > 0) {
            setFilters(prevState => ({ ...prevState, page: lastPage }));
        }
    }, [lastPage, filters, setFilters]);

    const handlePageChange = useCallback(
        page => () => {
            setFilters(oldValue => ({ ...oldValue, page }));
        },
        [setFilters]
    );

    const handleSortSubscriptions = useCallback(
        (property, direction) => {
            navigate('/subscriptions', { replace: true });
            setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
        },
        [navigate, setFilters]
    );

    const handleApplyFilters = useCallback(
        customFilters => {
            setFilters(prevState => ({ ...prevState, ...customFilters, page: 1 }));
        },
        [setFilters]
    );

    const handleToggleFilters = useCallback(() => setFiltersOpen(prevState => !prevState), []);

    const handleSubscriptionsListExport = useCallback(() => {
        const { enrichWith, ...importantFilters } = filters;
        exportSubscriptions(importantFilters);
    }, [exportSubscriptions, filters]);

    const handleCreateSubscription = useCallback(
        () => setNewSubscriptionOpen(prevState => !prevState),
        []
    );

    const handleIntegrateExternalSubscription = useCallback(
        () => navigate('/subscriptions/create-external', { replace: true }),
        [navigate]
    );

    return (
        <React.Fragment>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" align="left" gutterBottom>
                    {texts.ONGOING_SUBSCRIPTIONS}
                </Typography>
                {!dataIsLoading && (
                    <ActionButton
                        variant="outlined"
                        sx={listingActionButtonStyle}
                        onClick={handleSubscriptionsListExport}
                        disabled={exportLoading}
                    >
                        {texts.EXPORTER_LISTE}
                    </ActionButton>
                )}
            </Box>
            <SubscriptionInvite
                open={newSubscriptionOpen}
                handleToggle={handleCreateSubscription}
            />
            <ConfirmDialog
                open={alertTunnelChangesOpen}
                handleConfirm={handleCreateSubscriptionOpen}
                handleClose={handleCreateSubscriptionOpen}
                title={texts.TUNNEL_CHANGES_ALERT_TITLE}
                confirmText={texts.CLOSE}
                hideCancelButton
            >
                {texts.TUNNEL_CHANGES_ALERT_CONTENT.map(content => (
                    <Typography key={content} variant="body2" gutterBottom>
                        {content}
                    </Typography>
                ))}
            </ConfirmDialog>
            <CreateSubscriptionDialog
                open={createSubscriptionOpen}
                onClose={() => setCreateSubscriptionOpen(false)}
            />
            <Box display="flex" justifyContent="space-between" alignItems="center" marginY={3}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <QuickSearchField
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        handleClear={() => setKeyword('')}
                    />
                    <FiltersButton onClick={handleToggleFilters} />
                </Box>
                {userIsDistributor && (
                    <ActionButton
                        variant="outlined"
                        sx={listingActionButtonStyle}
                        onClick={handleAlertTunnelChangesOpen}
                    >
                        {texts.CREATE_SUBSCRIPTION}
                    </ActionButton>
                )}
                {((!userIsNetworkAdmin && userIsServicer) || userIsPromoter) && (
                    <Box display="flex" alignItems="flex-end" flexDirection="column" gap={1}>
                        {!userIsStandardServicer && !userIsPromoter && (
                            <ActionButton
                                variant="outlined"
                                sx={listingActionButtonStyle}
                                onClick={handleIntegrateExternalSubscription}
                            >
                                {texts.EXTERNAL_SUBSCRIPTION_INTEGRATION}
                            </ActionButton>
                        )}
                        <ActionButton
                            variant="outlined"
                            sx={listingActionButtonStyle}
                            onClick={handleCreateSubscription}
                        >
                            {texts.CREATE_SUBSCRIPTION}
                        </ActionButton>
                    </Box>
                )}
            </Box>
            {!dataIsLoading && debouncedKeyword && (
                <Typography variant="body2" gutterBottom>
                    {texts.RESULTS_FOR_KEYWORD(debouncedKeyword)}
                </Typography>
            )}
            <CustomFilters
                ongoing
                open={filtersOpen}
                applyFilters={handleApplyFilters}
                defaultFilters={defaultFilters}
                savedFilters={filters}
                loading={listLoader}
            />
            <TableContainer>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {filteredColumns.map(c => (
                            <col key={c.id} width={`${c.width}%`} />
                        ))}
                    </colgroup>
                    <EnhancedTableHead
                        headCells={filteredColumns}
                        orderBy={filters.orderBy}
                        direction={filters.direction}
                        handleSort={handleSortSubscriptions}
                    />
                    {subscriptionList.length === 0 || dataIsLoading ? (
                        <EmptyTableBody
                            colSpan={filteredColumns.length}
                            loading={dataIsLoading}
                            message={texts.NO_RESULTS_FOUND}
                        />
                    ) : (
                        <TableBody>
                            {subscriptionList.map(s => (
                                <SubscriptionRow key={s.id} subscription={s} />
                            ))}
                        </TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                loading={dataIsLoading}
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={filteredColumns.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </React.Fragment>
    );
};

export default SubscriptionTable;
